import { authErrorHandler } from "@/services/errorServices/authHandler.js";
import { getUserFriendlyErrorMessage } from '@/helpers/errorHandlerHelper.js';

/**
 * Vuex actions for the "alerts" (notifications) module.
 *
 * Fetches unread/read notifications and the unread count from the
 * notifications API (`/api/notifications*`) and commits the results via
 * updates.js mutations; also hosts local read/unread/clear bookkeeping that
 * doesn't need a round trip to the server. Also hosts module-local helpers
 * (below) for normalizing the unread-count response shape.
 */

/** Coerce `value` to a finite `Number`, returning `null` if it isn't one. */
const getNumericCount = (value) => {
  const count = Number(value);
  return Number.isFinite(count) ? count : null;
};

/**
 * Extract an unread-notification count from `/api/notifications/unread`'s
 * response, tolerating several possible response shapes: a bare array, a
 * bare number, or an object exposing the count under one of
 * `count`/`unreadCount`/`unreadNotificationsCount`/`total`/`result`, or as
 * the length of a `results`/`result`/`notifications` array. Defaults to `0`
 * if none of these match.
 */
const resolveUnreadCount = (data) => {
  if (Array.isArray(data)) return data.length;
  if (!data || typeof data !== 'object') return getNumericCount(data) ?? 0;

  const directCount = getNumericCount(
    data.count ??
    data.unreadCount ??
    data.unreadNotificationsCount ??
    data.total ??
    data.result
  );

  if (directCount !== null) return directCount;
  if (Array.isArray(data.results)) return data.count !== undefined ? getNumericCount(data.count) ?? data.results.length : data.results.length;
  if (Array.isArray(data.result)) return data.result.length;
  if (Array.isArray(data.notifications)) return data.notifications.length;

  return 0;
};

export default {
  /** Replace the aggregate `state.notifications` list wholesale with `value`. */
  addNotifications({ commit }, value) {
    commit('setNotifications', value);
  },
  /** Add a single `notification` to `state[type]` (see `setAddNotification`). */
  addNotification({ commit }, { notification, type }) {
    commit('setAddNotification', { notification, type });
  },
  /**
   * Remove a notification from `state[type]`. Note: Vuex only forwards a
   * single payload argument to a dispatched action, so `type` here is always
   * `undefined` when called via `dispatch('removeNotification', notification)`
   * — the `setRemoveNotification` mutation this calls expects `{ notification, type }`.
   */
  removeNotification({ commit, state }, notification, type) {
    commit('setRemoveNotification', notification, type);
  },
  /** Prepend new, not-yet-seen notifications onto `state.notifications` (see `setUpdateNotifications`). */
  updateNotifications({ commit }, value) {
    commit('setUpdateNotifications', value);
  },

  /** Mark the module as having completed its initial notifications load. */
  isLoadedNotifications({ commit }) {
    commit('setIsNotificationLoaded', true);
  },

  /** Overwrite `state.unreadNotificationsCount` with `value`. */
  addNotificationsCount({ commit }, value) {
    commit('setUnreadNotificationsCount', value);
  },

  /** Move `notification` from unread to read: drop it from the unread list, refresh the server-side unread count, then bump the local read count and append it to the read list. */
  async markAsRead({ commit, dispatch }, notification) {
    commit('setRemoveNotification', { notification, type: 'unreadNotifications' });
    await dispatch('fetchUnreadNotificationsCount');
    commit('incrementReadNotificationsCount');
    commit('setAddNotification', { notification, type: 'readNotifications' });
  },

  /** Reset all notification lists, counts, and error state (see `setClearNotifications`). */
  clearNotifications({ commit }) {
    commit('setClearNotifications');
  },

  /**
   * Fetch one page of notifications (`limit`/`offset`), filtered by `read`
   * (unread by default), and commit it into `unreadNotifications` or
   * `readNotifications` depending on `read`. On failure, sets
   * `notificationsError` and returns an error-shaped result instead of
   * throwing. Note the returned success shape (`{ newNotifications, count,
   * status }`) differs from the error shape (`{ notifications: [], count: 0,
   * status, message }`) — callers relying on a `notifications`/`count` field
   * across both branches should double check which one they get.
   */
  async fetchNotifications({ state, commit }, { limit = 10, offset = 0, read = false } = {}) {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    if (read !== undefined) {
      params.set('read', String(read));
    }

    try {
      // Loading state is intentionally disabled: notification lists refresh in the background.
      // commit('setNotificationsLoading', true);
      commit('setNotificationsError', null);
      const response = await fetch(`/api/notifications?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.status === 401 || response.status === 403) {
        authErrorHandler(response);
      }
      if (!response.ok) {
        const errorData = await response.json();
        const message = errorData?.message || 'Could not load notifications.';
        commit('setNotificationsError', message);
        console.error('Error response:', errorData);
        return { notifications: [], count: 0, status: 'error', message };
      }

      const data = await response.json();
      const typeCommitted = read ? 'setReadNotifications' : 'setUnreadNotifications';
      commit(typeCommitted, { ...data, offset });
      return { newNotifications: data.results, count: data.count, status: 'success' };
    } catch (error) {
      const message = getUserFriendlyErrorMessage(error) || 'Could not load notifications.';
      commit('setNotificationsError', message);
      console.error('Cannot get notifications:', error.message);
      return { notifications: [], count: 0, status: 'error', message };
    } finally {
      // commit('setNotificationsLoading', false);
    }
  },

  /** Fetch the unread notification count from `/api/notifications/unread`, normalize it via `resolveUnreadCount`, and commit it; sets `unreadCountError` on failure instead of throwing. */
  async fetchUnreadNotificationsCount({ commit }) {
    try {
      // Loading state is intentionally disabled: unread count refreshes in the background.
      // commit('setUnreadCountLoading', true);
      commit('setUnreadCountError', null);
      const response = await fetch(`/api/notifications/unread`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        commit('setUnreadNotificationsCount', resolveUnreadCount(data));
        return { status: 'success' };
      } else {
        const errorData = await response.json();
        const message = errorData?.message || 'Could not load unread notification count.';
        commit('setUnreadCountError', message);
        console.error('❌ Error fetching unread notifications count:', errorData.message);
        return { status: 'error', message };
      }
    } catch (error) {
      const message = getUserFriendlyErrorMessage(error) || 'Could not load unread notification count.';
      commit('setUnreadCountError', message);
      console.error('❌ Cannot get notifications:', error.message);
      return { status: 'error', message };
    } finally {
      // commit('setUnreadCountLoading', false);
    }
  },

  /** POST `notificationId` as read to the API; returns `true` on success or the error message string on failure. Does not commit any mutation itself — callers are responsible for updating local read/unread state. */
  async markNotificationAsRead({ commit }, notificationId) {
    try {
      const response = await fetch(`/api/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ read: true }),
      });

      if (response.ok) {
        return true;
        // emit('markAsRead', notificationId);
      } else {
        const errorData = await response.json();
        return errorData.message;
      }
    } catch (error) {
      console.error('Cannot mark notification as read:', error.message);
      return error.message;
    }
  },

  /**
   * Fetch `/api/notifications/unread` and commit the raw `results` array.
   * Note: `setUnreadNotifications` expects a `{ count, results, offset }`
   * payload, so passing the bare array here means `payload.count` and
   * `payload.results` are both `undefined` inside that mutation — the merge
   * into `state.unreadNotifications` is effectively a no-op.
   */
  async getUnreadNotifications({ commit }) {
    try {
      const response = await fetch('/api/notifications/unread');
      const data = await response.json();
      commit('setUnreadNotifications', data.results);
    } catch (error) {
      // In development, log the original error for debugging
      if (import.meta.env.MODE === 'development') {
        console.error('Error fetching unread notifications:', error);
      }
    }
  },

  /** Fetch the next page for the unread or read list (`payload.read`), using the current list length as the offset cursor. */
  async setNextPagonationNotifications({ dispatch, state }, payload) {
    await dispatch('fetchNotifications', {
      limit: 10,
      offset: payload.read ? state.readNotifications.length : state.unreadNotifications.length,
      read: payload.read,
    });
  },
};
